import { useEffect, useMemo, useRef } from "react"

import {
  SofiaChatMessage,
  SofiaComposer,
  SofiaMessageQueue,
  SofiaThinkingIndicator,
  SofiaTranscript,
  useCovi,
  useSetSofiaPanelContent,
  type SofiaTranscriptHandle,
} from "@/app-covi"
import { Button } from "@coval/ui"

import { useNotebookChat } from "../hooks/useNotebookChat"
import type { Notebook } from "../utils/notebookSnapshot"

interface NotebookChatProps {
  notebook: Notebook
  organizationSlug: string
  workspaceId: string
  locked: boolean
  onSaved: (notebook: Notebook) => void
  onBusyChange: (busy: boolean) => void
  onUnsentChange: (unsent: boolean) => void
}
const noop = () => {}

export function NotebookSofiaPanel({
  path,
  notebook,
  organizationSlug,
  workspaceId,
  locked,
  onSaved,
  onBusyChange,
  onUnsentChange,
}: NotebookChatProps & { path: string }) {
  const { open } = useCovi()
  const content = useMemo(
    () => ({
      path: path.split(/[?#]/)[0],
      title: notebook.title,
      getTranscript: () =>
        (notebook.chat ?? []).map((message, index) => ({
          kind: message.role,
          id: `${notebook.id}:${index}`,
          text: message.content,
        })),
      node: (
        <NotebookChat
          key={`${organizationSlug}:${workspaceId}:${notebook.id}`}
          notebook={notebook}
          organizationSlug={organizationSlug}
          workspaceId={workspaceId}
          locked={locked}
          onSaved={onSaved}
          onBusyChange={onBusyChange}
          onUnsentChange={onUnsentChange}
        />
      ),
    }),
    [
      path,
      notebook,
      organizationSlug,
      workspaceId,
      locked,
      onSaved,
      onBusyChange,
      onUnsentChange,
    ],
  )
  useSetSofiaPanelContent(content)
  useEffect(() => {
    open("prompt")
  }, [open, path])
  return null
}

function NotebookChat(props: NotebookChatProps) {
  const { notebook, organizationSlug, workspaceId, locked, onUnsentChange } =
    props
  const chat = useNotebookChat(props)
  const hasUnsentMessages = !!(
    chat.queue.length ||
    chat.failed ||
    chat.message.trim()
  )
  // The document and chat share one page-level navigation confirmation.
  useEffect(() => {
    onUnsentChange(hasUnsentMessages)
  }, [hasUnsentMessages, onUnsentChange])
  useEffect(() => () => onUnsentChange(false), [onUnsentChange])
  const transcriptRef = useRef<SofiaTranscriptHandle>(null)
  const lastSent = useRef<string | null>(null)
  return (
    <div className="flex min-h-0 flex-1 flex-col" aria-label="Notebook chat">
      <SofiaTranscript
        ref={transcriptRef}
        working={!!chat.pending}
        className="px-4 pb-4 pt-3"
      >
        <div className="space-y-4">
          {!notebook.chat?.length && (
            <SofiaChatMessage
              item={{
                kind: "assistant",
                id: `${notebook.id}:welcome`,
                text: "What would you like to create? I can investigate your Coval data, write findings, and keep refining this notebook with you.",
              }}
              orgSlug={organizationSlug}
              workspaceSlug={workspaceId}
            />
          )}
          {notebook.chat?.map((message, index) => (
            <SofiaChatMessage
              key={index}
              item={{
                kind: message.role,
                id: `${notebook.id}:chat:${index}`,
                text: message.content,
              }}
              orgSlug={organizationSlug}
              workspaceSlug={workspaceId}
            />
          ))}
          {chat.pending && (
            <SofiaChatMessage
              item={{
                kind: "user",
                id: `${notebook.id}:pending`,
                text: chat.pending,
              }}
              orgSlug={organizationSlug}
              workspaceSlug={workspaceId}
            />
          )}
          <SofiaThinkingIndicator visible={!!chat.pending} />
        </div>
      </SofiaTranscript>
      <div className="relative shrink-0 space-y-2 px-4 pb-3 pt-1">
        {chat.error && (
          <p role="alert" className="text-sm text-coval-danger-text">
            {chat.error} The queue is paused.
          </p>
        )}
        {chat.failed && (
          <div className="space-y-2 text-sm">
            <p>{chat.failed}</p>
            <Button variant="secondary" onClick={chat.retry}>
              Retry message
            </Button>
            <Button variant="ghost" onClick={chat.dismissFailure}>
              Skip message
            </Button>
          </div>
        )}
        <SofiaMessageQueue messages={chat.queue} onRemove={chat.removeQueued} />
        {(locked || notebook.locked) && (
          <p className="text-sm text-coval-text-muted">
            {notebook.locked
              ? "Unlock the notebook to let Sofia make changes."
              : "Your messages will send after your document changes are saved."}
          </p>
        )}
        <SofiaComposer
          value={chat.message}
          onValueChange={chat.setMessage}
          clearOnSubmit={false}
          maxLength={6000}
          allowQueue
          disabled={!!notebook.locked}
          isStreaming={!!chat.pending}
          lastSentTextRef={lastSent}
          onDraftPresenceChange={noop}
          onSlashCommandUsed={noop}
          onSubmit={() => {
            lastSent.current = chat.message
            transcriptRef.current?.scrollToLatest()
            chat.send()
          }}
        />
      </div>
    </div>
  )
}
